import { useState } from "react";
import PropertyPicker from "@/components/PropertyPicker";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Building2 } from "lucide-react";

/**
 * Dialog to move a report or notice into a property (Kohde).
 * kind: "report" | "notice" | "bulk-reports"
 *   - "report" / "notice": single item, itemId + optional currentPropertyId
 *   - "bulk-reports": itemIds is an array of report_id — uses /reports/bulk-move
 *
 * (Component name preserved for backward-compat with existing imports;
 *  the UI no longer references "kansio".)
 */
export default function MoveToFolderDialog({ kind, itemId, itemIds, currentPropertyId, onClose, onMoved }) {
  const [propertyId, setPropertyId] = useState(currentPropertyId || null);
  const [busy, setBusy] = useState(false);
  const isBulk = kind === "bulk-reports";
  const count = isBulk ? (itemIds || []).length : 1;

  const save = async () => {
    if (isBulk && count === 0) return;
    setBusy(true);
    try {
      if (isBulk) {
        const { data } = await api.post("/reports/bulk-move", {
          report_ids: itemIds,
          property_id: propertyId,
        });
        toast.success(`Siirretty ${data.moved} työtä`);
        onMoved?.(propertyId, data.moved);
      } else {
        const url = kind === "report" ? `/reports/${itemId}` : `/notices/${itemId}`;
        const body = kind === "report" ? { property_id: propertyId } : { folder_id: propertyId };
        await api.patch(url, body);
        toast.success("Siirretty kohteeseen");
        onMoved?.(propertyId);
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Siirto epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="move-folder-dialog">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md my-8 max-h-[calc(100vh-4rem)] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="inline-flex items-center gap-2">
            <Building2 className="h-4 w-4 text-emerald-600" />
            <div className="font-display font-semibold text-slate-900">
              {isBulk ? `Siirrä ${count} työtä kohteeseen` : "Siirrä kohteeseen"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="move-folder-close">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">
          <PropertyPicker value={propertyId} onChange={setPropertyId} label="Valitse kohde (tai luo uusi)" />
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
          <button onClick={onClose} className="btn-ghost text-sm" data-testid="move-folder-cancel">Peruuta</button>
          <button onClick={save} disabled={busy || (isBulk && count === 0)} className="btn-primary text-sm disabled:opacity-50" data-testid="move-folder-save">
            {busy ? "Siirretään..." : (isBulk ? `Siirrä ${count} työtä` : "Siirrä")}
          </button>
        </div>
      </div>
    </div>
  );
}
